import React, { memo } from 'react';
import { useQuery } from '@apollo/client';
import { GET_ACTIVE_FLAG_TEMPLATES } from '../../../graphql/flagTemplates';
import JobCategoryBadge from './JobCategoryBadge';
import JobStatusBadge from './JobStatusBadge';
import FlipBadge from './FlipBadge';
import ReadyForInspectionBadge from './ReadyForInspectionBadge';
import DateOverrideBadge from './DateOverrideBadge';
import LateCheckoutBadge from './LateCheckoutBadge';
import { OwnerArrivalIndicator, OwnerDepartureIndicator, isOwnerArrival, isOwnerDeparture } from './OwnerIndicators';
import { 
  NeedsInspectionIndicator, 
  AreaIndicator, 
  EarlyCheckinIndicator,
  MaintenanceStartIndicator,
  MaintenanceEndingIndicator
} from '../../job-indicators';
import { isLateCheckout, isEarlyCheckin } from '../../../utils/job-utils';
import { isMaintenanceStart, isMaintenanceEnding } from '../../../utils/job-badge-utils';
import { useLateCheckoutForCustomJob } from '../../../hooks/useLateCheckoutForCustomJob';
import CustomFlagBadge from './CustomFlagBadge';

// Helper function to check if an inspection job has a ready cleaning job
function hasReadyCleaningJob(job, allJobs) {
  // Must be an inspection job
  if (job.category !== 'INSPECTION') return false;
  
  // First check if the job has associatedCleaningJobStatus from the GraphQL query
  if (job.associatedCleaningJobStatus) {
    return job.associatedCleaningJobStatus === 'READY_FOR_INSPECTION' || 
           job.associatedCleaningJobStatus === 'COMPLETED';
  }
  
  // Fallback: Look for cleaning job in the same property and date
  if (!job.property?.id || !job.scheduledDate) return false;
  
  const jobDate = new Date(job.scheduledDate).toDateString();
  
  // Look for a cleaning job on the same property and date that's ready for inspection or completed
  const readyCleaningJob = allJobs.find(otherJob => 
    otherJob.id !== job.id &&
    otherJob.category === 'CLEANING' &&
    otherJob.property?.id === job.property.id &&
    new Date(otherJob.scheduledDate).toDateString() === jobDate &&
    (otherJob.status === 'READY_FOR_INSPECTION' || otherJob.status === 'COMPLETED')
  );
  
  return !!readyCleaningJob;
}

// Helper function to check if a job has a ready cleaning job
function hasReadyJob(job, allJobs) {
  // For inspection jobs, check if there's a ready cleaning job
  if (job.category === 'INSPECTION') {
    return hasReadyCleaningJob(job, allJobs);
  }
  
  // For other job types, we might add similar logic in the future
  return false;
}

function JobBadges({ job, allJobs = [], showAddedFromCompleted = false }) {
  // Hook for CHECKIN job late checkout detection - now used for ALL CHECKIN jobs
  const { hasLateCheckout: backendLateCheckout } = useLateCheckoutForCustomJob(job);
  
  // Fetch flag templates to pass to CustomFlagIndicator for template lookup
  const { data: templatesData } = useQuery(GET_ACTIVE_FLAG_TEMPLATES);
  const flagTemplates = templatesData?.getActiveFlagTemplates || [];
  
  // For CHECKIN jobs, prioritize backend detection over frontend logic
  // This ensures consistent detection when jobs are moved to checkout dates
  const shouldShowLateCheckout = job.serviceType?.timeBase === 'CHECKIN' 
    ? backendLateCheckout 
    : (isLateCheckout(job) || backendLateCheckout);
  
  return (
    <>
      {/* Pass serviceType and cleaningType to JobCategoryBadge */}
      <JobCategoryBadge 
        category={job.category} 
        serviceType={job.serviceType || job.serviceTypeId} 
        cleaningType={job.cleaningType} 
      />
      <JobStatusBadge status={job.status} />
      
      {/* Note: Clean Completed indicator now shows above job title, not as a badge */}
      
      {/* Show badge for jobs that were added from completed section */}
      {showAddedFromCompleted && job._addedFromCompleted && (
        <NeedsInspectionIndicator />
      )}
      
      {/* Show Flip badge - now using computed isFlip property */}
      {job.isFlip && <FlipBadge job={job} />}
      
      {/* Show Date Override badge - exclude for Pre-arrival Inspection jobs */}
      {job.hasDateOverride && !(job.category === 'INSPECTION' && job.serviceType?.title?.toLowerCase().includes('pre-arrival')) && <DateOverrideBadge />}
      
      {/* Show Late Checkout badge - uses backend detection for CHECKIN jobs, fallback for others */}
      {shouldShowLateCheckout && <LateCheckoutBadge />}
      
      {/* Show Early Check-in badge - now uses backend resolver field */}
      {isEarlyCheckin(job) && <EarlyCheckinIndicator />}
      
      {/* Show Owner badges */}
      {isOwnerDeparture(job) && <OwnerDepartureIndicator />}
      {isOwnerArrival(job) && <OwnerArrivalIndicator />}
      
      {/* Show Maintenance badges */}
      {isMaintenanceStart(job) && <MaintenanceStartIndicator />}
      {isMaintenanceEnding(job) && <MaintenanceEndingIndicator />}
      
      {/* Show Custom Flags */}
      {job.customFlags?.map(flag => (
        <CustomFlagBadge 
          key={flag.id} 
          flag={flag}
          flagTemplates={flagTemplates}
        />
      ))}
    </>
  );
}

// Export memoized component with simplified comparison
export default memo(JobBadges, (prevProps, nextProps) => {
  // Simple comparison - just check if the job object changed
  return (
    prevProps.job === nextProps.job &&
    prevProps.allJobs === nextProps.allJobs &&
    prevProps.showAddedFromCompleted === nextProps.showAddedFromCompleted &&
    // Also check if custom flags changed
    JSON.stringify(prevProps.job?.customFlags) === JSON.stringify(nextProps.job?.customFlags)
  );
});